import { Banner, Button, Field, Input } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useAuth } from '../contexts/AuthContext';
import { BRAND_NAME, BRAND_LOGO } from '../lib/brand';
import { SocialIcon, type SocialProvider } from '../components/SocialIcon';
import { TurnstileWidget, useTurnstileSiteKey } from '../components/TurnstileWidget';
import { PasswordField } from '../components/ui/PasswordField';
import { ICONS } from '../components/ui/icons';

type Step = 'login' | 'mfa' | 'forgot' | 'forgot-sent';

interface Providers {
  oauth: Record<SocialProvider, boolean>;
  passkey: boolean;
  passwordReset: boolean;
}

const SOCIAL: Array<{ id: SocialProvider; label: string }> = [
  { id: 'google', label: 'Google' },
  { id: 'microsoft', label: 'Microsoft' },
  { id: 'github', label: 'GitHub' },
];

/** Última forma de entrar usada neste navegador (selo "Último usado") */
type LastMethod = SocialProvider | 'passkey' | 'password';
const LAST_KEY = 'ingrave-last-login';
const PENDING_KEY = 'ingrave-pending-login';
function readLast(): { method: LastMethod; email?: string } | null {
  try { const v = JSON.parse(localStorage.getItem(LAST_KEY) || 'null'); return v && v.method ? v : null; } catch { return null; }
}
function saveLast(method: LastMethod, email?: string) {
  try { localStorage.setItem(LAST_KEY, JSON.stringify({ method, ...(email ? { email } : {}), at: new Date().toISOString() })); } catch { /* sem armazenamento */ }
}
const LastBadge = () => <span className="auth-last-badge">Último usado</span>;


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Login numa tela só, no estilo da Cloudflare: social/passkey, e-mail e senha; login
 * social e passkey; verificação em duas etapas quando a conta tem.
 */
export function LoginPage() {
  const { login, startSession } = useAuth();
  const [step, setStep] = useState<Step>('login');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [useRecovery, setUseRecovery] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [providers, setProviders] = useState<Providers | null>(null);
  const [last] = useState(readLast);
  const turnstileKey = useTurnstileSiteKey();
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  // o token é de uso único: depois de cada tentativa, novo desafio
  const nextCaptcha = () => { setCaptcha(null); setCaptchaReset((n) => n + 1); };
  const needCaptcha = !!turnstileKey && !captcha;

  useEffect(() => {
    if (last?.method === 'password' && last.email) setEmail((cur) => cur || last.email!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch('/api/auth/providers').then((r) => (r.ok ? r.json() : null)).then((d) => d && setProviders(d)).catch(() => undefined);
  }, []);

  // Volta do login social: resultado no fragmento da URL
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    if (![...hash.keys()].length) return;
    window.history.replaceState(null, '', window.location.pathname);
    const token = hash.get('token');
    if (token) {
      setBusy('oauth');
      try { const p = sessionStorage.getItem(PENDING_KEY); if (p) saveLast(p as LastMethod); sessionStorage.removeItem(PENDING_KEY); } catch { /* sem armazenamento */ }
      startSession(token).catch(() => setError('Não foi possível abrir a sessão. Tente de novo.')).finally(() => setBusy(null));
      return;
    }
    if (hash.get('mfa_token')) {
      setMfaToken(hash.get('mfa_token')!);
      setStep('mfa');
      return;
    }
    if (hash.get('error')) setError(hash.get('error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (next: Step) => { setError(null); setStep(next); };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) return setError('Informe um e-mail válido');
    setBusy('password');
    setError(null);
    const result = await login(email.trim(), senha, captcha);
    setBusy(null);
    if (turnstileKey) nextCaptcha();
    if (result.status === 'ok') saveLast('password', remember ? email.trim() : undefined);
    if (result.status === 'mfa') {
      setMfaToken(result.mfaToken);
      setCode('');
      go('mfa');
    } else if (result.status === 'error') {
      setError(result.message);
    }
  };

  const submitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('mfa');
    setError(null);
    try {
      const res = await fetch('/api/auth/mfa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Código inválido');
      let pending: string | null = null;
      try { pending = sessionStorage.getItem(PENDING_KEY); sessionStorage.removeItem(PENDING_KEY); } catch { /* sem armazenamento */ }
      saveLast((pending as LastMethod) || 'password', pending ? undefined : email.trim() || undefined);
      await startSession(data.data.token, data.data.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Código inválido');
      setBusy(null);
    }
  };

  const loginWithPasskey = async () => {
    setBusy('passkey');
    setError(null);
    try {
      const optRes = await fetch('/api/auth/passkey/options', { method: 'POST' });
      const opt = await optRes.json();
      const response = await startAuthentication({ optionsJSON: opt.options });
      const res = await fetch('/api/auth/passkey/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeToken: opt.challengeToken, response }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Não foi possível entrar com a passkey');
      saveLast('passkey');
      await startSession(data.data.token, data.data.user);
    } catch (err) {
      const name = (err as { name?: string })?.name;
      if (name !== 'NotAllowedError' && name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Não foi possível entrar com a passkey');
      }
      setBusy(null);
    }
  };

  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) return setError('Informe um e-mail válido');
    setBusy('forgot');
    await fetch('/api/auth/password/forgot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), ...(captcha ? { turnstileToken: captcha } : {}) }),
    }).catch(() => undefined);
    if (turnstileKey) nextCaptcha();
    setBusy(null);
    go('forgot-sent');
  };

  const socialEnabled = SOCIAL.filter((s) => providers?.oauth?.[s.id]);
  const passkeyAvailable = browserSupportsWebAuthn();

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <img src={BRAND_LOGO} alt={BRAND_NAME} style={{ height: 28, width: 'auto' }} className="[[data-mode=dark]_&]:invert" />
        </div>

        {error && <div className="mb-4"><Banner variant="error" description={<>{error}</>} /></div>}

        {step === 'login' && (
          <>
            <h2 className="text-xl font-semibold auth-title" style={{ textAlign: 'center', marginBottom: 20 }}>Fazer login</h2>
            {(socialEnabled.length > 0 || passkeyAvailable) && (
              <div className="auth-alt">
                {socialEnabled.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${socialEnabled.length}, 1fr)`, gap: 8 }}>
                    {socialEnabled.map((s) => (
                      <a key={s.id} className="auth-alt__button" href={`/api/auth/oauth/${s.id}/start`}
                        onClick={() => { setBusy(s.id); try { sessionStorage.setItem(PENDING_KEY, s.id); } catch { /* sem armazenamento */ } }}>
                        <SocialIcon provider={s.id} />
                        <span>{s.label}</span>
                        {last?.method === s.id && <LastBadge />}
                      </a>
                    ))}
                  </div>
                )}
                {passkeyAvailable && (
                  <button type="button" className="auth-alt__button" onClick={loginWithPasskey} disabled={busy === 'passkey'}>
                    <ICONS.Key size={16} />
                    <span>{busy === 'passkey' ? 'Aguardando a passkey…' : 'Entrar com passkey'}</span>
                    {last?.method === 'passkey' && <LastBadge />}
                  </button>
                )}
                <div className="auth-divider"><span>ou</span></div>
              </div>
            )}
            <form onSubmit={submitPassword} className="flex flex-col gap-4">
              {last?.method === 'password' && (
                <div className="flex items-center gap-2" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  <LastBadge /> Você entrou com e-mail e senha da última vez
                </div>
              )}
              <Field label="E-mail">
                <Input className="w-full" onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="username webauthn" placeholder="nome@empresa.com.br" value={email} autoFocus />
              </Field>
              <PasswordField label="Senha" autoComplete="current-password" value={senha} onChange={setSenha} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                Lembrar meu acesso neste dispositivo
              </label>
              {turnstileKey && <TurnstileWidget key="t-login" siteKey={turnstileKey} action="login" onToken={setCaptcha} resetKey={captchaReset} />}
              <Button type="submit" variant="primary" className="w-full justify-center" loading={busy === 'password' || busy === 'oauth'} disabled={!email || !senha || needCaptcha}>
                {needCaptcha ? 'Verificando…' : 'Fazer login'}
              </Button>
              {providers?.passwordReset && (
                <button type="button" className="auth-link" style={{ alignSelf: 'center' }} onClick={() => go('forgot')}>
                  Esqueceu a senha?
                </button>
              )}
            </form>
          </>
        )}

        {step === 'mfa' && (
          <form onSubmit={submitMfa} className="flex flex-col gap-5">
            <div>
              <h2 className="text-xl font-semibold auth-title">Verificação em duas etapas</h2>
              <div style={{ color: 'var(--text-muted)', marginTop: 6 }}>
                {useRecovery ? 'Digite um dos códigos de recuperação que você guardou.' : 'Digite o código de 6 dígitos do seu aplicativo autenticador.'}
              </div>
            </div>
            <Field label={useRecovery ? 'Código de recuperação' : 'Código'}>
              <Input onChange={(e) => setCode(e.target.value)} inputMode={useRecovery ? 'text' : 'numeric'} autoComplete="one-time-code" placeholder={useRecovery ? 'xxxxx-xxxxx' : '000000'} value={code} autoFocus />
            </Field>
            <Button type="submit" variant="primary" loading={busy === 'mfa'} disabled={code.trim().length < 6}>Verificar</Button>
            <button type="button" className="auth-link" style={{ alignSelf: 'flex-start' }} onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError(null); }}>
              {useRecovery ? 'Usar o aplicativo autenticador' : 'Usar um código de recuperação'}
            </button>
          </form>
        )}

        {step === 'forgot' && (
          <form onSubmit={submitForgot} className="flex flex-col gap-5">
            <div>
              <h2 className="text-xl font-semibold auth-title">Redefinir senha</h2>
              <div style={{ color: 'var(--text-muted)', marginTop: 6 }}>Enviaremos um link para criar uma nova senha.</div>
            </div>
            <Field label="E-mail">
              <Input onChange={(e) => setEmail(e.target.value)} type="email" value={email} autoFocus />
            </Field>
            {turnstileKey && <TurnstileWidget key="t-forgot" siteKey={turnstileKey} action="forgot" onToken={setCaptcha} resetKey={captchaReset} />}
            <Button type="submit" variant="primary" loading={busy === 'forgot'} disabled={needCaptcha}>{needCaptcha ? 'Verificando…' : 'Enviar link'}</Button>
            <button type="button" className="auth-link" style={{ alignSelf: 'flex-start' }} onClick={() => go('login')}>Voltar</button>
          </form>
        )}

        {step === 'forgot-sent' && (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="text-xl font-semibold auth-title">Confira seu e-mail</h2>
              <div style={{ color: 'var(--text-muted)', marginTop: 6 }}>
                Se houver uma conta com <strong>{email}</strong>, enviamos um link para redefinir a senha. Ele vale por 1 hora.
              </div>
            </div>
            <Button variant="secondary" onClick={() => go('login')}>Voltar ao login</Button>
          </div>
        )}

        {step === 'login' && (
          <div style={{ marginTop: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
            Ainda não tem conta? <Link to="/cadastro" style={{ color: 'var(--link)', fontWeight: 600 }}>Teste grátis por 7 dias</Link>
          </div>
        )}
      </div>
    </div>
  );
}
